import React, { useCallback, useMemo } from 'react';
import { MdAdd, MdMoreHoriz, MdSearch } from 'react-icons/md';
import { Tooltip } from '@aibuild/ui';
import { RAIL_TOOLTIP } from '@components/SidebarRail';
import { useNodeDefinitions } from '../../hooks/useNodeDefinitions';
import type { NodeDefinitionDto } from '@api/nodeDefinitions';
import {
  PALETTE_DRAG_LABEL_MIME,
  PALETTE_DRAG_MIME,
  dispatchPaletteAddEvent,
  type PaletteType,
} from '../paletteDrop';
import { resolveIcon } from './iconRegistry';
import {
  AddButton,
  BrandMark,
  BrandStack,
  IconStack,
  Logo,
  MoreSlot,
  QuickAddStack,
  RailButton,
  RailRoot,
  SearchButton,
  SrOnly,
  TooltipFrame,
  WorkspaceLabel,
} from './ProcessEditorRail.styled';

interface ProcessEditorRailProps {
  /** Whether the NodeLibrary flyout is currently open — drives the
   *  `is-open` styling of the Search toggle. */
  flyoutOpen: boolean;
  /** Toggle the NodeLibrary flyout (Search, More). */
  onToggleFlyout: () => void;
  /** Open the "New process" template picker. */
  onOpenNewProcessDialog: () => void;
  /** Expand AND auto-pop the ProductSwitcher dropdown. */
  onSwitchWorkspace: () => void;
  /**
   * When true, every action button on the rail is disabled (matches the
   * prototype's published-process treatment) — EXCEPT the OS workspace
   * switcher and the brand mark. Switching workspace is a navigation,
   * not a graph-mutating action; the design preserves it so the user
   * can still leave the locked editor without typing the URL.
   */
  disabled?: boolean;
  dataTestId?: string;
}

/**
 * Build a custom drag preview that mirrors the canvas-node header (icon
 * chip + label) instead of the raw button screenshot. The icon DOM is
 * cloned from the live source button so the preview always reflects the
 * correct glyph regardless of which palette type is being dragged.
 *
 * Mounted off-screen so the browser can snapshot it for the duration of
 * the drag, then removed on the next tick.
 *
 * Keeping the preview in inline HTML (rather than a React portal) so the
 * preview survives the synchronous browser snapshot of `setDragImage`.
 */
const buildDragPreview = (
  label: string,
  sourceIconDomNode: Element | null,
): HTMLElement => {
  const ghost = document.createElement('div');
  ghost.style.position = 'absolute';
  ghost.style.top = '-1000px';
  ghost.style.left = '-1000px';
  ghost.style.display = 'inline-flex';
  ghost.style.alignItems = 'center';
  ghost.style.gap = '10px';
  ghost.style.padding = '8px 14px 8px 9px';
  ghost.style.borderRadius = '999px';
  ghost.style.background = '#13141E';
  ghost.style.border = '1px solid #464555';
  ghost.style.color = '#E4E1ED';
  ghost.style.font = '600 13.5px/1 system-ui, sans-serif';
  ghost.style.boxShadow = '0 12px 24px rgba(0,0,0,0.45)';
  ghost.style.whiteSpace = 'nowrap';

  const iconChip = document.createElement('span');
  iconChip.style.display = 'inline-flex';
  iconChip.style.alignItems = 'center';
  iconChip.style.justifyContent = 'center';
  iconChip.style.width = '30px';
  iconChip.style.height = '30px';
  iconChip.style.borderRadius = '50%';
  iconChip.style.background = 'linear-gradient(180deg,#343548,#28293a)';
  iconChip.style.border = '1px solid #404058';
  iconChip.style.color = '#d4d0f2';
  if (sourceIconDomNode) {
    const cloned = sourceIconDomNode.cloneNode(true) as HTMLElement;
    cloned.style.width = '18px';
    cloned.style.height = '18px';
    iconChip.appendChild(cloned);
  }
  ghost.appendChild(iconChip);

  const labelSpan = document.createElement('span');
  labelSpan.style.paddingRight = '4px';
  labelSpan.textContent = label;
  ghost.appendChild(labelSpan);

  return ghost;
};

/**
 * Resolve a `NodeDefinitionDto` to the palette string the canvas
 * expects. Mirrors `paletteTypeFor` inside `NodeLibrary` so the rail and
 * the flyout produce matching drop payloads.
 */
const paletteTypeFor = (def: NodeDefinitionDto): PaletteType => {
  switch (def.kind) {
    case 'input':
    case 'output':
    case 'gate':
    case 'note':
    case 'image_gen':
    case '3d_gen':
    case 'manual':
      return def.kind;
    case 'task':
    default:
      return def.id === 'task' ? 'task:_' : `task:${def.id}`;
  }
};

const dataTestIdFor = (def: NodeDefinitionDto): string =>
  def.id.replaceAll(/\W+/g, '-').toLowerCase();

/**
 * The collapsed-rail variant of the editor sidebar. Mirrors the
 * prototype's `.nav--collapsed` shape:
 *
 *   - Top:    Aibuild logo + workspace label ("OS") + tiny expand button
 *   - Middle: Search toggle (opens NodeLibrary flyout) + "+" (new process)
 *   - Below:  per-node-type quick-add icons (drag onto canvas), with a
 *             trailing "More" (3-dots) button that also opens the flyout
 *
 * The rail is purely presentational + drag-source — every click handler
 * is threaded down from the parent (`ProcessEditorSidebar`) so the rail
 * can be mounted in tests with simple spies.
 */
const ProcessEditorRail: React.FC<ProcessEditorRailProps> = ({
  flyoutOpen,
  onToggleFlyout,
  onOpenNewProcessDialog,
  onSwitchWorkspace,
  disabled = false,
  dataTestId = 'process-editor-rail',
}) => {
  const definitionsQuery = useNodeDefinitions();

  const quickAddNodes = useMemo(() => {
    const data = definitionsQuery.data ?? [];
    return data.filter((d) => !d.deprecated);
  }, [definitionsQuery.data]);

  const onQuickAddDragStart = useCallback(
    (event: React.DragEvent<HTMLButtonElement>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      const button = event.currentTarget;
      const paletteType = button.dataset.paletteType as PaletteType | undefined;
      const label = button.dataset.label ?? '';
      if (!paletteType) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.setData(PALETTE_DRAG_MIME, paletteType);
      event.dataTransfer.setData(PALETTE_DRAG_LABEL_MIME, label);
      event.dataTransfer.effectAllowed = 'move';
      try {
        const sourceIcon = button.querySelector('svg');
        const ghost = buildDragPreview(label, sourceIcon);
        document.body.appendChild(ghost);
        event.dataTransfer.setDragImage(ghost, 24, 22);
        // Schedule removal on the next tick — the browser has already
        // snapshotted the element by then.
        setTimeout(() => ghost.remove(), 0);
      } catch {
        // Some browsers (jsdom in tests) don't implement setDragImage —
        // the drag still works without the custom preview.
      }
    },
    [disabled],
  );

  const onQuickAddClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      const button = event.currentTarget;
      const paletteType = button.dataset.paletteType;
      const label = button.dataset.label ?? '';
      if (!paletteType) return;
      // Cross-tree communication via window CustomEvent — the
      // GraphCanvas listens for this and places the node at the
      // viewport centre (nudging if it overlaps anything existing).
      dispatchPaletteAddEvent({ paletteType, label });
    },
    [disabled],
  );

  return (
    <RailRoot data-testid={dataTestId} aria-label="Process editor rail">
      <BrandStack>
        <BrandMark
          to="/"
          aria-label="Go to home"
          data-testid={`${dataTestId}__logo-link`}
        >
          <Logo />
        </BrandMark>
        <Tooltip
          content="Switch workspace · Aibuild OS"
          {...RAIL_TOOLTIP}
        >
          <WorkspaceLabel
            type="button"
            onClick={onSwitchWorkspace}
            aria-label="Switch workspace · Aibuild OS"
            data-testid={`${dataTestId}__workspace-switcher`}
          >
            OS
          </WorkspaceLabel>
        </Tooltip>
      </BrandStack>

      <IconStack>
        <Tooltip
          content={
            disabled
              ? 'Library unavailable while the editor is locked'
              : 'Search nodes'
          }
          {...RAIL_TOOLTIP}
        >
          <TooltipFrame>
            <SearchButton
              type="button"
              $isOpen={flyoutOpen}
              onClick={disabled ? undefined : onToggleFlyout}
              aria-label="Search node library"
              aria-pressed={flyoutOpen}
              aria-expanded={flyoutOpen}
              aria-disabled={disabled || undefined}
              disabled={disabled}
              data-testid={`${dataTestId}__search`}
            >
              <MdSearch aria-hidden="true" />
            </SearchButton>
          </TooltipFrame>
        </Tooltip>

        <Tooltip
          content={
            disabled
              ? 'New process unavailable while the editor is locked'
              : 'New process from template'
          }
          {...RAIL_TOOLTIP}
        >
          <TooltipFrame>
            <AddButton
              type="button"
              onClick={disabled ? undefined : onOpenNewProcessDialog}
              aria-label="New process"
              aria-disabled={disabled || undefined}
              disabled={disabled}
              data-testid={`${dataTestId}__new-process`}
            >
              <MdAdd aria-hidden="true" />
            </AddButton>
          </TooltipFrame>
        </Tooltip>
      </IconStack>

      <QuickAddStack data-testid={`${dataTestId}__quick-add`}>
        {quickAddNodes.map((def) => {
          const Icon = resolveIcon(def.displayMetadata.iconId);
          const palette = paletteTypeFor(def);
          const label = def.displayMetadata.label;
          const tip = disabled
            ? 'Adding nodes is unavailable while the editor is locked'
            : label;
          return (
            <Tooltip key={def.id} content={tip} {...RAIL_TOOLTIP}>
              <TooltipFrame>
                <RailButton
                  type="button"
                  draggable={!disabled}
                  onDragStart={onQuickAddDragStart}
                  onClick={onQuickAddClick}
                  aria-label={`Add ${label} node`}
                  aria-disabled={disabled || undefined}
                  disabled={disabled}
                  data-palette-type={palette}
                  data-label={label}
                  data-testid={`${dataTestId}__quick-add-${dataTestIdFor(def)}`}
                >
                  <Icon />
                  <SrOnly>
                    Click to add a {label} node, or drag to place it
                  </SrOnly>
                </RailButton>
              </TooltipFrame>
            </Tooltip>
          );
        })}
        {/* "More" 3-dots opens the full NodeLibrary flyout. It lives at
            the end of the quick-add column so when the list is short it
            sits right after the last item with the stack's natural 4px
            gap (no empty rail above it). When the list is tall enough
            to scroll, MoreSlot's `position: sticky; bottom: 0` keeps
            the dots painted at the bottom of the scroll container so
            the affordance never disappears off-screen. */}
        <MoreSlot data-testid={`${dataTestId}__more-slot`}>
          <Tooltip
            content={
              disabled
                ? 'Library unavailable while the editor is locked'
                : 'More nodes'
            }
            {...RAIL_TOOLTIP}
          >
            <TooltipFrame>
              <RailButton
                type="button"
                onClick={disabled ? undefined : onToggleFlyout}
                aria-label="More nodes"
                aria-pressed={flyoutOpen}
                aria-expanded={flyoutOpen}
                aria-disabled={disabled || undefined}
                disabled={disabled}
                data-testid={`${dataTestId}__more`}
              >
                <MdMoreHoriz aria-hidden="true" />
              </RailButton>
            </TooltipFrame>
          </Tooltip>
        </MoreSlot>
      </QuickAddStack>
    </RailRoot>
  );
};

export default ProcessEditorRail;
